import { MembershipOfferFragment, Offer_Engagement_Type, OfferFragment } from '@occc/graphql';
import { MarketingContentImgPreview, Typography } from '@occc-app/design-system';

import { MakeTableFields } from '@occc/ui-design-system';
import {
  earningMechanismWithProgressTypenames,
  OFFER_MAX_PUNCH_CARD_REQUIRED_PROGRESS,
} from './in-progress-offers.constants';
import {
  EarningMechanismTypename,
  EarningMechanismWithProgressTypename,
  GetOfferProgressVariantParams,
  OfferMemberProgress,
  OfferProgressVariant,
} from './in-progress-offers.types';
import { OfferProgress } from './components/offer-progress.component';

const roundToTwo = (num: number) => parseFloat(num.toFixed(2));

const getIsEarningMechanismTypenameIsWithProgress = (
  typename: EarningMechanismTypename,
): typename is EarningMechanismWithProgressTypename => {
  return earningMechanismWithProgressTypenames.includes(typename);
};

export const makeFields: MakeTableFields<MembershipOfferFragment> = (offer) => {
  const offerProgress = getOfferMembershipProgress(offer.earningMechanism);

  const getProgressVariant = () => {
    if (
      !offerProgress ||
      !offer.earningMechanism ||
      !getIsEarningMechanismTypenameIsWithProgress(offer.earningMechanism.__typename)
    ) {
      return null;
    }

    return getOfferProgressVariant({
      earningMechanismTypename: offer.earningMechanism?.__typename,
      engagementType: offer.engagementType ?? null,
      memberProgress: offerProgress,
    });
  };

  const progressVariant = getProgressVariant();

  const isVarietyPurchaseOverTimeWithMultipleProducts = offer.earningMechanism
    ? getIsVarietyPurchaseOverTimeWithMultipleProducts(offer.earningMechanism)
    : false;

  const getProgressValue = () => {
    if (offerProgress && progressVariant) {
      return <OfferProgress progress={offerProgress} variant={progressVariant} />;
    }

    if (isVarietyPurchaseOverTimeWithMultipleProducts) {
      return <Typography variant='body2'>Variety purchase coming soon</Typography>;
    }

    return '';
  };

  const progressValue = getProgressValue();

  return [
    {
      label: 'Image',
      value: (
        <MarketingContentImgPreview
          imageUrl={offer.marketingContents?.[0]?.imageUrl ?? undefined}
        />
      ),
    },
    {
      label: 'Name',
      value: offer.name,
    },
    {
      label: 'Description',
      value: offer.description ?? '',
    },
    {
      label: 'Progress',
      value: offerProgress?.achieved ?? '',
      align: 'right',
    },
    {
      label: 'Total Req',
      value: offerProgress?.required ?? '',
      align: 'right',
    },
    {
      label: '',
      value: progressValue,
      width: '20%',
    },
  ];
};

/**
 * Check if there is a technical reason to show a bar (floats, huge numbers in progress)
 */
const getIsThereTechnicalReasonToShowBar = ({ required, achieved }: OfferMemberProgress) => {
  if (required > OFFER_MAX_PUNCH_CARD_REQUIRED_PROGRESS) {
    return true;
  }

  if (!Number.isInteger(achieved) || !Number.isInteger(required)) {
    return true;
  }

  return false;
};

const getOfferProgressVariant = ({
  earningMechanismTypename,
  engagementType,
  memberProgress,
}: GetOfferProgressVariantParams): OfferProgressVariant => {
  if (getIsThereTechnicalReasonToShowBar(memberProgress)) {
    return 'bar';
  }

  if (earningMechanismTypename === 'EarningMechanismTotalSpendOverTime') {
    return 'bar';
  }

  if (
    earningMechanismTypename === 'EarningMechanismVarietyPurchaseOverTime' &&
    engagementType !== Offer_Engagement_Type.PunchcardChallenge
  ) {
    return 'bar';
  }

  return 'punches';
};

const getIsVarietyPurchaseOverTimeWithMultipleProducts = (
  mechanism: Exclude<OfferFragment['earningMechanism'], null | undefined>,
) => {
  return (
    mechanism.__typename === 'EarningMechanismVarietyPurchaseOverTime' &&
    mechanism.varietyMemberProgress.length > 1
  );
};

const getOfferMembershipProgress = (
  mechanism: OfferFragment['earningMechanism'],
): OfferMemberProgress | null => {
  if (!mechanism) {
    return null;
  }

  let progress: OfferMemberProgress | null = null;

  if (mechanism?.__typename === 'EarningMechanismVisits' && mechanism?.visitsMemberProgress) {
    progress = {
      required: roundToTwo(mechanism.visitsMemberProgress.visitsRequired),
      achieved: roundToTwo(mechanism.visitsMemberProgress.visits),
    };
  }

  if (
    (mechanism?.__typename === 'EarningMechanismTotalSpendOverTime' ||
      mechanism?.__typename === 'EarningMechanismTotalEligiblePurchaseOverTime') &&
    mechanism?.purchaseMemberProgress
  ) {
    progress = {
      required: roundToTwo(mechanism.purchaseMemberProgress.quantityRequired),
      achieved: roundToTwo(mechanism.purchaseMemberProgress.quantityPurchased),
    };
  }

  // TODO: varietyMemberProgress for multiple product challenge is not supported yet
  // Example: 2 coffees + 2 donuts => free coffee and donut
  if (
    mechanism.__typename === 'EarningMechanismVarietyPurchaseOverTime' &&
    mechanism.varietyMemberProgress.length === 1
  ) {
    const firstProgress = mechanism.varietyMemberProgress[0];
    progress = {
      required: roundToTwo(firstProgress.quantityRequired),
      achieved: roundToTwo(firstProgress.quantityPurchased),
    };
  }

  return progress?.required ? progress : null;
};
